import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sitesService, costingPolicyService, multiLocationService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import StocktakeSettingsPanel from '../components/StocktakeSettingsPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Settings, Save, Loader2, ArrowLeft, Scale, Shield, Info, MapPin, Plus, Trash2 } from 'lucide-react';
import { getApiError } from '@/lib/utils';
import SitePicker from '../components/SitePicker';

export default function StocktakeSettingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_counting_method: '',
    allow_roll_back_forward_valuations: false,
    default_stock_forward_sheet_sort_order: '',
    default_session_type: '',
    default_audit_mode: '',
  });

  // iter369 — Wave 3 #1 — Audit Locations list state. Lives next to
  // the stocktake settings for the same site since both are part of
  // "house rules" the site admin manages once.
  const [auditLocations, setAuditLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [newLocationDraft, setNewLocationDraft] = useState({ name: '', location_type: 'standard' });
  const [savingLocation, setSavingLocation] = useState(false);

  // Costing policy state
  const [effectiveMethod, setEffectiveMethod] = useState(null);
  const [sitePolicy, setSitePolicy] = useState(null);
  const [methods, setMethods] = useState([]);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [siteOverrideEnabled, setSiteOverrideEnabled] = useState(false);
  const [siteOverrideForm, setSiteOverrideForm] = useState({
    costing_method: 'wac',
    allow_reference_cost_fallback: false,
  });

  useEffect(() => {
    const loadSites = async () => {
      try {
        const [data, methodsData] = await Promise.all([
          sitesService.list({ limit: 500 }),
          costingPolicyService.getMethods(),
        ]);
        setSites(Array.isArray(data) ? data : data.sites || data.items || []);
        setMethods(methodsData.methods || []);
        // iter370 #3 — Honour ?site=<id> so the CTA from the create-
        // session dialog lands the admin straight on the right site.
        const params = new URLSearchParams(window.location.search);
        const preselect = params.get('site');
        if (preselect) setSelectedSiteId(preselect);
      } catch { /* silent */ }
    };
    loadSites();
  }, []);

  const loadSettings = useCallback(async (siteId) => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [data, effective, policy, locations] = await Promise.all([
        sitesService.getStocktakeSettings(Number(siteId)),
        costingPolicyService.getEffective(Number(siteId)),
        costingPolicyService.getSitePolicy(Number(siteId)),
        multiLocationService.listLocations(Number(siteId)).catch(() => []),
      ]);
      if (data) {
        setForm({
          default_counting_method: data.default_counting_method || '',
          allow_roll_back_forward_valuations: data.allow_roll_back_forward_valuations || false,
          default_stock_forward_sheet_sort_order: data.default_stock_forward_sheet_sort_order || '',
          default_session_type: data.default_session_type || '',
          default_audit_mode: data.default_audit_mode || '',
        });
      } else {
        setForm({ default_counting_method: '', allow_roll_back_forward_valuations: false, default_stock_forward_sheet_sort_order: '', default_session_type: '', default_audit_mode: '' });
      }
      setEffectiveMethod(effective);
      setSitePolicy(policy);
      setAuditLocations(Array.isArray(locations) ? locations : []);
      if (policy?.has_override) {
        setSiteOverrideEnabled(true);
        setSiteOverrideForm({
          costing_method: policy.costing_method || 'wac',
          allow_reference_cost_fallback: policy.allow_reference_cost_fallback || false,
        });
      } else {
        setSiteOverrideEnabled(false);
        setSiteOverrideForm({ costing_method: 'wac', allow_reference_cost_fallback: false });
      }
    } catch (err) {
      console.error('Failed to load stocktake settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSiteId) loadSettings(selectedSiteId);
  }, [selectedSiteId, loadSettings]);

  const handleSave = async () => {
    if (!selectedSiteId) return;
    setSaving(true);
    try {
      await sitesService.upsertStocktakeSettings(Number(selectedSiteId), {
        default_counting_method: form.default_counting_method || null,
        allow_roll_back_forward_valuations: form.allow_roll_back_forward_valuations,
        default_stock_forward_sheet_sort_order: form.default_stock_forward_sheet_sort_order || null,
        default_session_type: form.default_session_type || null,
        default_audit_mode: form.default_audit_mode || null,
      });
      toast({ title: 'Saved', description: 'Stocktake settings updated' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSitePolicy = async () => {
    if (!selectedSiteId) return;
    setSavingPolicy(true);
    try {
      if (siteOverrideEnabled) {
        await costingPolicyService.setSitePolicy(Number(selectedSiteId), siteOverrideForm);
        toast({ title: 'Saved', description: 'Site costing override applied' });
      } else {
        await costingPolicyService.removeSitePolicy(Number(selectedSiteId));
        toast({ title: 'Saved', description: 'Site costing override removed — inheriting from group/organisation' });
      }
      // Reload effective method
      const effective = await costingPolicyService.getEffective(Number(selectedSiteId));
      setEffectiveMethod(effective);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save costing policy'), variant: 'destructive' });
    } finally {
      setSavingPolicy(false);
    }
  };

  const selectedSiteName = sites.find(s => String(s.id) === selectedSiteId)?.name;

  const SOURCE_BADGES = {
    global: { label: 'Org Default', className: 'bg-slate-100 text-slate-700' },
    group: { label: 'Group Override', className: 'bg-blue-100 text-blue-700' },
    site: { label: 'Site Override', className: 'bg-emerald-100 text-emerald-700' },
  };

  return (
    <div className="space-y-6" data-testid="stocktake-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="h-6 w-6 text-slate-600" />
              Site Stocktake Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure stocktake parameters and costing policy per site
            </p>
          </div>
        </div>
        {selectedSiteId && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-stocktake-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Select Site</CardTitle>
        </CardHeader>
        <CardContent>
          <SitePicker
            sites={sites}
            value={selectedSiteId}
            onChange={setSelectedSiteId}
            placeholder="Search for a site..."
            testId="settings-site-select"
          />
        </CardContent>
      </Card>

      {selectedSiteId && !loading && (
        <>
          {/* Effective Costing Method — read only */}
          <Card data-testid="effective-costing-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Scale className="h-4 w-4 text-blue-600" />
                    Effective Costing Method
                  </CardTitle>
                  <CardDescription>The resolved costing method for this site after applying all overrides</CardDescription>
                </div>
                {effectiveMethod && (
                  <Badge
                    variant="outline"
                    className={SOURCE_BADGES[effectiveMethod.source_level]?.className || ''}
                    data-testid="effective-method-source-badge"
                  >
                    {SOURCE_BADGES[effectiveMethod.source_level]?.label || effectiveMethod.source_label}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {effectiveMethod ? (
                <div className="flex items-center gap-4" data-testid="effective-method-display">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex-1">
                    <p className="text-lg font-semibold text-blue-900" data-testid="effective-method-label">
                      {effectiveMethod.method_label}
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Source: {effectiveMethod.source_label}
                      {effectiveMethod.allow_reference_cost_fallback && ' | Reference cost fallback enabled'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading...</p>
              )}
            </CardContent>
          </Card>

          {/* iter369 — Wave 3 #1 — Audit defaults card */}
          <Card data-testid="audit-defaults-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4 text-indigo-600" />
                Default Audit Type
              </CardTitle>
              <CardDescription>
                Applied automatically when a new stocktake session is created for this site. The auditor can still override on the create-session screen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Default Session Type</Label>
                  <Select value={form.default_session_type || '__none__'} onValueChange={v => setForm({ ...form, default_session_type: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="default-session-type-select"><SelectValue placeholder="No default — ask each time" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No default — ask each time</SelectItem>
                      <SelectItem value="valuation">Valuation</SelectItem>
                      <SelectItem value="stock_audit">Stock Audit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Default Audit Mode</Label>
                  <Select value={form.default_audit_mode || '__none__'} onValueChange={v => setForm({ ...form, default_audit_mode: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="default-audit-mode-select"><SelectValue placeholder="No default — ask each time" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No default — ask each time</SelectItem>
                      <SelectItem value="single">Single location</SelectItem>
                      <SelectItem value="multi_with_cellar">Multi-location with cellar</SelectItem>
                      <SelectItem value="multi_no_cellar">Multi-location without cellar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                Click <strong>Save Settings</strong> at the top to persist these defaults.
              </p>
            </CardContent>
          </Card>

          {/* iter369 — Wave 3 #1 — Audit Locations editor (only relevant
              when the default audit mode is multi_*). */}
          {(form.default_audit_mode === 'multi_with_cellar' || form.default_audit_mode === 'multi_no_cellar' || auditLocations.length > 0) && (
            <Card data-testid="audit-locations-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-rose-600" />
                  Audit Locations
                </CardTitle>
                <CardDescription>
                  Reusable locations attached to this site. Used by multi-location audits to split count sheets, deliveries and issues per area (e.g. cellar, bar, restaurant). Auditors are still assigned per session.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {auditLocations.length === 0 && (
                  <p className="text-sm text-muted-foreground">No locations defined yet — add at least one cellar and one consumption location below.</p>
                )}
                {auditLocations.length > 0 && (
                  <div className="border rounded-lg divide-y" data-testid="audit-locations-list">
                    {auditLocations.map(loc => (
                      <div key={loc.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`audit-location-row-${loc.id}`}>
                        <div>
                          <span className="font-medium">{loc.name}</span>
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {loc.location_type === 'cellar' ? 'Cellar' : 'Standard'}
                          </Badge>
                          {!loc.is_active && <Badge variant="outline" className="ml-1 text-[10px] bg-rose-50 text-rose-700">Inactive</Badge>}
                        </div>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-rose-500 hover:text-rose-700"
                          disabled={savingLocation}
                          onClick={async () => {
                            if (!window.confirm(`Delete location "${loc.name}"? This cannot be undone if no audits reference it.`)) return;
                            setSavingLocation(true);
                            try {
                              await multiLocationService.deleteLocation(loc.id);
                              setAuditLocations(prev => prev.filter(l => l.id !== loc.id));
                              toast({ title: 'Location removed' });
                            } catch (err) {
                              toast({ title: 'Could not delete', description: getApiError(err, 'It may be in use by an existing audit.'), variant: 'destructive' });
                            } finally {
                              setSavingLocation(false);
                            }
                          }}
                          data-testid={`audit-location-delete-${loc.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2 pt-2">
                  <div className="flex-1">
                    <Label className="text-xs">Location name</Label>
                    <Input
                      value={newLocationDraft.name}
                      onChange={e => setNewLocationDraft(d => ({ ...d, name: e.target.value }))}
                      placeholder="e.g. Main Bar"
                      data-testid="new-location-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={newLocationDraft.location_type} onValueChange={v => setNewLocationDraft(d => ({ ...d, location_type: v }))}>
                      <SelectTrigger className="w-32" data-testid="new-location-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="cellar">Cellar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    disabled={savingLocation || !newLocationDraft.name.trim()}
                    onClick={async () => {
                      setSavingLocation(true);
                      try {
                        const created = await multiLocationService.createLocation({
                          site_id: Number(selectedSiteId),
                          name: newLocationDraft.name.trim(),
                          location_type: newLocationDraft.location_type,
                        });
                        setAuditLocations(prev => [...prev, created].sort((a, b) =>
                          (a.location_type === 'cellar' ? -1 : 0) - (b.location_type === 'cellar' ? -1 : 0) || a.name.localeCompare(b.name)
                        ));
                        setNewLocationDraft({ name: '', location_type: 'standard' });
                        toast({ title: 'Location added' });
                      } catch (err) {
                        toast({ title: 'Could not add', description: getApiError(err, 'Please try again'), variant: 'destructive' });
                      } finally {
                        setSavingLocation(false);
                      }
                    }}
                    data-testid="add-location-btn"
                  >
                    {savingLocation ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  </Button>
                </div>
                {(form.default_audit_mode === 'multi_with_cellar') && !auditLocations.some(l => l.location_type === 'cellar') && (
                  <p className="text-[11px] text-amber-600">⚠ Multi-with-cellar mode needs at least one location of type "cellar".</p>
                )}
              </CardContent>
            </Card>
          )}
          {isAdmin && (
            <Card data-testid="site-costing-override-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4 text-emerald-600" />
                      Site Costing Override
                    </CardTitle>
                    <CardDescription>
                      Override the organisation/group default costing method for this specific site
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleSaveSitePolicy}
                    disabled={savingPolicy}
                    size="sm"
                    data-testid="save-site-policy-btn"
                  >
                    {savingPolicy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={siteOverrideEnabled}
                    onCheckedChange={setSiteOverrideEnabled}
                    data-testid="site-override-toggle"
                  />
                  <Label className="text-sm">
                    {siteOverrideEnabled
                      ? 'This site uses a custom costing method'
                      : 'This site inherits from group/organisation default'}
                  </Label>
                </div>

                {siteOverrideEnabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
                    <div className="space-y-2">
                      <Label>Costing Method</Label>
                      <Select
                        value={siteOverrideForm.costing_method}
                        onValueChange={(v) => setSiteOverrideForm(f => ({ ...f, costing_method: v }))}
                      >
                        <SelectTrigger data-testid="site-costing-method-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {methods.map(m => (
                            <SelectItem key={m.value} value={m.value}>
                              <div className="flex items-center gap-2">
                                {m.full_label}
                                {!m.available && (
                                  <Badge variant="outline" className="text-[10px] py-0 ml-1 text-amber-600 border-amber-300">Pending</Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const m = methods.find(x => x.value === siteOverrideForm.costing_method);
                        return m && !m.available ? (
                          <div className="flex items-start gap-1.5 text-xs text-amber-600 bg-amber-50 rounded p-2">
                            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>{m.label} engine is not yet live. WAC will be used as fallback.</span>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <div className="space-y-2">
                      <Label>Reference Cost Fallback</Label>
                      <div className="flex items-center gap-3 pt-1">
                        <Switch
                          checked={siteOverrideForm.allow_reference_cost_fallback}
                          onCheckedChange={(v) => setSiteOverrideForm(f => ({ ...f, allow_reference_cost_fallback: v }))}
                          data-testid="site-reference-fallback-switch"
                        />
                        <span className="text-sm">
                          {siteOverrideForm.allow_reference_cost_fallback ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Existing settings panel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{selectedSiteName}</CardTitle>
            </CardHeader>
            <CardContent>
              <StocktakeSettingsPanel form={form} onChange={setForm} loading={loading} />
            </CardContent>
          </Card>
        </>
      )}

      {selectedSiteId && loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {!selectedSiteId && (
        <div className="text-center py-12 text-muted-foreground">
          <Settings className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Select a site above to view and edit its stocktake settings</p>
        </div>
      )}
    </div>
  );
}
